import type { BridgeError, NotebookBridgeHandle } from '@/pages/scripts/hooks/notebook-bridge-client'

import { getAssistantSurface } from './assistant-actions'

/** The server's cap on a notebook id in the page context. */
const MAX_ID = 64

export const NOT_OPEN =
  'The notebook is not open in a live session on this page. Open it (show_notebook view notebook) and its session first.'
export const NOT_CONNECTED =
  'The notebook tab has not connected to the assistant yet. Wait a moment and try again; if it keeps failing, reload the session.'
export const NO_ANSWER = 'The notebook tab did not answer; reload it'

export type Refusal = { success: false; error: string; message: string; notebook_id?: string } & Record<string, unknown>

export const invalidInput = (message: string, notebookId?: string): Refusal => ({
  success: false,
  error: 'invalid',
  message,
  ...(notebookId ? { notebook_id: notebookId } : {}),
})

/**
 * The bridge of the notebook named by the call, re-checked in the browser exactly as the server
 * checked the page context: this page's notebook, in a ready session, with the bridge ready.
 */
export function openNotebookBridge(input: unknown): { notebookId: string; bridge: NotebookBridgeHandle } | Refusal {
  const raw = (input ?? {}) as { notebook_id?: unknown }
  const notebookId = typeof raw.notebook_id === 'string' ? raw.notebook_id.trim() : ''
  if (!notebookId || notebookId.length > MAX_ID) return invalidInput('`notebook_id` is required.')

  const handle = getAssistantSurface()
  const open = handle?.kind === 'scripts' ? handle.readContext().scripts.notebook : undefined
  const notOpen: Refusal = { success: false, error: 'not_open', notebook_id: notebookId, message: NOT_OPEN }

  if (handle?.kind !== 'scripts' || !open || open.id !== notebookId || open.session !== 'ready') return notOpen

  const bridge = handle.bridge
  if (!bridge || !bridge.ready) return { ...notOpen, message: NOT_CONNECTED }

  return { notebookId, bridge }
}

const isBridgeError = (e: unknown): e is BridgeError =>
  typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string'

/** A rejected bridge request as a tool result; a timeout tells the model to have the tab reloaded. */
export function bridgeFailure(notebookId: string, err: unknown): Refusal {
  if (!isBridgeError(err)) {
    return {
      success: false,
      error: 'bridge_error',
      notebook_id: notebookId,
      message: err instanceof Error ? err.message : String(err),
    }
  }

  if (err.code === 'bridge_timeout') {
    return { success: false, error: 'bridge_timeout', notebook_id: notebookId, message: NO_ANSWER }
  }

  if (err.code === 'bridge_not_ready' || err.code === 'bridge_closed') {
    return { success: false, error: 'not_open', notebook_id: notebookId, message: NOT_CONNECTED }
  }

  return {
    success: false,
    error: err.code,
    notebook_id: notebookId,
    message: err.message,
    ...(typeof err.op_index === 'number' ? { op_index: err.op_index } : {}),
  }
}
